import { isHumanChatSendRetry, type HumanChatSendRetry } from './humanChatSendRetry';
import { isHumanChatCommandRetries, type HumanChatCommandRetry } from './humanChatCommandRetry';

export type HumanChatRecovery = {
  token: string;
  sid: string;
  callId: string;
  draft?: string;
  sendRetry?: HumanChatSendRetry;
  commandRetries?: HumanChatCommandRetry[];
} & ({ checking: true; preparing?: never; conversationId?: never }
  | { checking?: never; preparing: true; conversationId?: never }
  | { checking?: never; preparing?: never; conversationId: string });

export class HumanChatRecoveryStore {
  private readonly key: string;
  hadCheckpoint = false;

  constructor(private readonly storage: Storage, private readonly scope: string, private readonly clock = Date.now, private readonly publicKey?: string) {
    this.key = `autoqm:human-chat:v1:${scope}`;
  }

  private valid(value: { sid?: unknown; callId?: unknown; token?: unknown; checking?: unknown; preparing?: unknown;
    conversationId?: unknown; draft?: unknown; sendRetry?: unknown; commandRetries?: unknown }): boolean {
    if (!value || ![value.sid, value.callId].every(entry => typeof entry === 'string' && entry.length > 0 && entry.length <= 200)
      || typeof value.token !== 'string' || value.token.length > 16384) return false;
    if (value.checking === true ? value.preparing !== undefined || value.conversationId !== undefined
      : value.checking !== undefined || (value.preparing === true ? value.conversationId !== undefined
        : value.preparing !== undefined || typeof value.conversationId !== 'string' || !value.conversationId || value.conversationId.length > 200)) return false;
    if (value.draft !== undefined && (typeof value.draft !== 'string' || value.draft.length > 8000)) return false;
    if (value.sendRetry !== undefined && (!isHumanChatSendRetry(value.sendRetry) || value.sendRetry.conversationId !== value.conversationId)) return false;
    if (value.commandRetries !== undefined && (!value.conversationId || !isHumanChatCommandRetries(value.commandRetries)
      || value.commandRetries.some(command => command.conversationId !== value.conversationId))) return false;
    try {
      const parts = value.token.split('.');
      if (parts.length !== 3) return false;
      const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      const now = Math.floor(this.clock() / 1000);
      const issuedAt = claims.chatIssuedAt === undefined ? claims.iat : claims.chatIssuedAt;
      return claims.sid === value.sid && (!this.publicKey || claims.pub === this.publicKey)
        && (claims.chatConversationId === undefined || (typeof claims.chatConversationId === 'string'
          && claims.chatConversationId.length > 0 && claims.chatConversationId === value.conversationId && claims.chatHistoryConversationId === undefined))
        && (claims.chatHistoryConversationId === undefined || (typeof claims.chatHistoryConversationId === 'string'
          && claims.chatHistoryConversationId.length > 0 && claims.chatHistoryConversationId === value.conversationId))
        && Number.isSafeInteger(claims.exp) && Number.isSafeInteger(claims.iat)
        && Number.isSafeInteger(issuedAt) && issuedAt > 0 && issuedAt <= claims.iat && claims.iat <= now
        && claims.exp > claims.iat && claims.exp <= claims.iat + 3600
        && (claims.exp + 300 >= now || typeof value.conversationId === 'string'
          || value.checking === true && /^nrt-return-[a-f0-9]{64}$/.test(claims.sid)) && issuedAt + 86400 > now;
    } catch { return false; }
  }

  read(): HumanChatRecovery | null {
    try {
      const raw = this.storage.getItem(this.key);
      if (raw === null) return null;
      this.hadCheckpoint = true;
      if (raw.length > 160000) { this.block(); return null; }
      const record = JSON.parse(raw);
      if (record.version !== 1 || record.scope !== this.scope || !this.valid(record.session)) { this.block(); return null; }
      return this.snapshot(record.session);
    } catch { if (this.hadCheckpoint) this.block(); return null; }
  }

  private block(): void {
    try { this.storage.setItem(this.key, JSON.stringify({ version: 1, scope: this.scope, session: null })); } catch { this.clear(); }
  }

  private snapshot(session: HumanChatRecovery): HumanChatRecovery {
    const { token, sid, callId, conversationId, checking, preparing, draft, sendRetry, commandRetries } = session;
    const content = { ...(draft !== undefined ? { draft } : {}), ...(sendRetry ? { sendRetry: {
      text: sendRetry.text, commandId: sendRetry.commandId, epoch: sendRetry.epoch, conversationId: sendRetry.conversationId,
    } } : {}), ...(commandRetries ? { commandRetries: commandRetries.map(({ action, commandId, conversationId, epoch }) => ({ action, commandId, conversationId, epoch })) } : {}) };
    return checking === true ? { token, sid, callId, checking: true, ...content }
      : preparing === true ? { token, sid, callId, preparing: true, ...content } : { token, sid, callId, conversationId, ...content };
  }

  write(session: HumanChatRecovery): boolean {
    try {
      if (!this.valid(session)) { this.block(); return false; }
      const checkpoint = this.snapshot(session);
      const serialized = JSON.stringify({ version: 1, scope: this.scope, session: checkpoint });
      if (serialized.length > 160000) return false;
      this.storage.setItem(this.key, serialized);
      return this.storage.getItem(this.key) === serialized;
    } catch { return false; }
  }

  replace(session: HumanChatRecovery): boolean {
    return this.valid(session) && this.write(session);
  }

  interrupt(): boolean {
    try {
      const serialized = JSON.stringify({ version: 1, scope: this.scope, session: null });
      if (serialized.length > 160000) return false;
      this.storage.setItem(this.key, serialized);
      return this.storage.getItem(this.key) === serialized;
    } catch { return false; }
  }

  clear(): void {
    try { this.storage.removeItem(this.key); } catch {}
  }
}